import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { last, nth, split } from 'lodash';
import { useMemo } from 'react';
import { DateField, FunctionField, ReferenceField, useTranslate } from 'react-admin';
import useGetAvatarTraceabilityHistory from '@/hooks/useGetAvatarTraceabilityHistory';
import { METADATA_AVATAR_TYPES } from '@/utils/CONST';
import extractIdFromURI from '@/utils/id';
import DisordersTreatmentNote from '@/components/common/notes/disorders-treatment-note/disorders-treatment-note';

interface Props {
    item: any;
    index?: number;
    /** 'dialog': una columna y menos padding para panel lateral / modal. 'export': PDF (por defecto). */
    variant?: 'export' | 'dialog';
}

const DisordersTreatmentExportItem = ({ item, variant = 'export' }: Props) => {
    const translate = useTranslate();

    const renderLabelValue = (label: React.ReactNode, value: React.ReactNode) => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, auto)',
                gap: 1,
                justifyContent: 'start',
            }}
        >
            <Typography
                variant='body2'
                sx={{
                    fontWeight: 500,
                    color: 'text.secondary',
                }}
            >
                {label}
            </Typography>

            <Typography
                variant='body2'
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontWeight: 400,
                    color: 'text.primary',
                }}
            >
                <Box
                    component='span'
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        '& > *:not(:last-child)': {
                            marginRight: '4px',
                        },
                    }}
                >
                    {value}
                </Box>
            </Typography>
        </Box>
    );
    const { history } = useGetAvatarTraceabilityHistory({
        avatarId: extractIdFromURI(item.id) ?? '',
    });

    const secondNote: any = useMemo(() => nth(history, 2), [history]);

    const isDialog = variant === 'dialog';

    return (
        <Box
            sx={{
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.300',
                p: isDialog ? 2 : 2.5,
                display: 'grid',
                gap: isDialog ? 2 : 3,
                gridTemplateColumns: isDialog ? '1fr' : 'repeat(2, 1fr)',
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                    alignContent: 'center',
                }}
            >
                {renderLabelValue(
                    `${translate('disorder.list.number')}:`,
                    <>
                        {split(item.alphaId, ':')[1]} {translate('common.words.of', { _: 'du' })}
                        <Box> </Box>
                        <DateField
                            record={{
                                date: item.creationDate,
                            }}
                            options={{
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                            }}
                            source={'date'}
                            sx={{
                                lineHeight: 1,
                                fontSize: 14,
                                fontWeight: 400,
                            }}
                        />
                    </>,
                )}

                {renderLabelValue(
                    `${translate('disorder.list.type')}:`,
                    <ReferenceField
                        record={item}
                        source='metadataAvatarType'
                        reference={METADATA_AVATAR_TYPES}
                        label={'disorder.list.type'}
                        sx={{
                            '& span': {
                                fontSize: 14,
                                lineHeight: 1.5,
                                fontWeight: 400,
                            },
                        }}
                    >
                        <FunctionField
                            render={({ name }: { name: string }) =>
                                translate(`disorder.list.${last(split(name, ':'))}`)
                            }
                        />
                    </ReferenceField>,
                )}

                {renderLabelValue(`${translate('disorder.list.subtype')}:`, item.name)}
            </Box>

            <DisordersTreatmentNote note={secondNote} avatarName={item?.name} withTitle={false} fromExport />
        </Box>
    );
};

export default DisordersTreatmentExportItem;
